import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { uploadEntityAttachment } from '@/api/common';
import { deviceDetailsKeys } from '@/features/devices/hooks';
import {
  createRepair,
  deleteRepair,
  fetchDeviceRepairs,
  repairAttachments,
  updateRepair,
} from '../api';
import type { CreateRepairRequest, UpdateRepairRequest } from '../types';

export const repairHistoryKeys = {
  all: ['repairHistory'] as const,
  byDevice: (deviceId: string) => [...repairHistoryKeys.all, 'device', deviceId] as const,
};

// useDeviceRepairs loads every repair entry belonging to a device.
export function useDeviceRepairs(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: repairHistoryKeys.byDevice(deviceId),
    queryFn: () => fetchDeviceRepairs(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// useCreateRepair adds a new repair entry to a device and refreshes the views.
export function useCreateRepair(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (request: CreateRepairRequest) => createRepair(request),
    onSuccess: () => {
      invalidateRepairViews(queryClient, deviceId);
      toast.success(t('repairHistory.toast.createSuccess'));
    },
    onError: () => toast.error(t('repairHistory.toast.createError')),
  });
}

// useUpdateRepair patches the changed entry fields of one repair.
export function useUpdateRepair(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ repairId, request }: { repairId: string; request: UpdateRepairRequest }) =>
      updateRepair(repairId, request),
    onSuccess: () => {
      invalidateRepairViews(queryClient, deviceId);
      toast.success(t('repairHistory.toast.updateSuccess'));
    },
    onError: () => toast.error(t('repairHistory.toast.updateError')),
  });
}

// useDeleteRepair removes a repair entry and its document.
export function useDeleteRepair(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (repairId: string) => deleteRepair(repairId),
    onSuccess: () => {
      invalidateRepairViews(queryClient, deviceId);
      toast.success(t('repairHistory.toast.deleteSuccess'));
    },
    onError: () => toast.error(t('repairHistory.toast.deleteError')),
  });
}

// useUploadRepairDocument runs the three-step attachment upload for a repair. Uploading a new
// document exchanges the entry's single document (the backend supersedes the previous one).
// onProgress feeds the panel's loading bar.
export function useUploadRepairDocument(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      repairId,
      file,
      onProgress,
    }: {
      repairId: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      await uploadEntityAttachment(repairAttachments(repairId), {
        file,
        fallbackMimeType: 'application/pdf',
        onProgress,
      });
    },
    onSuccess: () => {
      invalidateRepairViews(queryClient, deviceId);
      toast.success(t('repairHistory.toast.uploadSuccess'));
    },
    onError: () => toast.error(t('repairHistory.toast.uploadError')),
  });
}

// invalidateRepairViews refreshes the repair list and the device details that embed it.
function invalidateRepairViews(
  queryClient: ReturnType<typeof useQueryClient>,
  deviceId: string
) {
  queryClient.invalidateQueries({ queryKey: repairHistoryKeys.byDevice(deviceId) });
  queryClient.invalidateQueries({ queryKey: deviceDetailsKeys.detail(deviceId) });
}
